import React, { useEffect, useState } from 'react';

import { fleetAPI } from '../lib/api';
import { useCan } from '../hooks/useCan';
import BoxTile from '../components/fleet/BoxTile';
import EnrolmentPanel from '../components/fleet/EnrolmentPanel';
import { POLL_MS, apiError, toneStyle } from '../components/fleet/fleetUi';

/**
 * Fleet: every CamNode box in the account as a camera grid (PRD F8), polled
 * every 2 s like the daemon tool pages. Tiles sort by name; a red or green
 * frame is tally, a grey tile is offline.
 */
export default function FleetPage() {
  const canManage = useCan('fleet:manage');
  const [boxes, setBoxes] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    const load = () => fleetAPI.boxes()
      .then((res) => { if (!cancelled) { setBoxes(res.data.boxes); setError(''); setNow(Date.now()); } })
      .catch((err) => { if (!cancelled) setError(apiError(err, 'Could not load the fleet')); });
    load();
    const timer = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  const list = boxes || [];
  const online = list.filter((b) => b.online).length;
  const offline = list.length - online;
  const alerts = list.flatMap((b) => b.alerts.map((a) => ({ ...a, box: b })));
  const critical = alerts.filter((a) => a.severity === 'critical');

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-app-fg">Fleet</h1>
          <p className="text-sm text-gray-500 mt-0.5">The account&apos;s CamNode camera boxes.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Count label="online" value={online} tone={online ? 'ok' : 'off'} />
          <Count label="offline" value={offline} tone={offline ? 'bad' : 'off'} />
          <Count label={alerts.length === 1 ? 'alert' : 'alerts'} value={alerts.length} tone={critical.length ? 'bad' : alerts.length ? 'warn' : 'off'} />
          {canManage && !adding && (
            <button onClick={() => setAdding(true)} className="ml-1 px-3 py-1.5 text-sm rounded font-medium text-white" style={{ background: 'var(--blue)' }}>
              Add a box
            </button>
          )}
        </div>
      </div>

      {critical.length > 0 && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm border" style={{ ...toneStyle('bad', 10), borderColor: 'color-mix(in srgb, var(--live) 35%, transparent)' }}>
          <div className="font-semibold">{critical.length} critical alert{critical.length === 1 ? '' : 's'}</div>
          <ul className="mt-1 space-y-0.5">
            {critical.slice(0, 6).map((a) => (
              <li key={`${a.box.id}-${a.code}-${a.service || ''}`} className="text-app-fg">
                <span className="font-medium">{a.box.name}</span>
                <span className="text-gray-400"> · {a.message}</span>
              </li>
            ))}
            {critical.length > 6 && <li className="text-gray-400">and {critical.length - 6} more</li>}
          </ul>
        </div>
      )}

      {error && (
        <div className="mb-4 px-4 py-3 rounded text-sm" style={toneStyle('bad', 10)}>{error}</div>
      )}

      {adding && <EnrolmentPanel onClose={() => setAdding(false)} />}

      {boxes === null && !error && <p className="text-sm text-gray-500">Loading…</p>}

      {boxes && boxes.length === 0 && (
        <div className="max-w-xl mx-auto mt-10 text-center rounded-lg border border-dashed border-gray-700 px-6 py-10">
          <h2 className="text-base font-semibold text-app-fg">No boxes yet</h2>
          <p className="text-sm text-gray-400 mt-2">
            A CamNode box joins this account with an enrolment code. Make a code here, then on the box open its web page,
            go to Fleet, set the dashboard URL to <span className="font-mono text-[13px] text-gray-300 break-all">{window.location.origin}</span> and
            paste the code. It appears here within a few seconds, and from then on reports every 2 seconds.
          </p>
          {canManage
            ? !adding && <button onClick={() => setAdding(true)} className="mt-4 px-4 py-2 text-sm rounded font-medium text-white" style={{ background: 'var(--blue)' }}>Add a box</button>
            : <p className="text-xs text-gray-500 mt-4">Only an owner or admin of the account can make enrolment codes.</p>}
        </div>
      )}

      {list.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
          {list.map((box) => <BoxTile key={box.id} box={box} now={now} />)}
        </div>
      )}
    </div>
  );
}

function Count({ label, value, tone }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded text-xs" style={toneStyle(tone, 12)}>
      <span className="font-mono text-[13px] font-semibold">{value}</span>
      <span>{label}</span>
    </span>
  );
}
